import React, { useCallback, memo } from 'react';
import { isFunction } from 'lodash';
import { asStyledComponent } from '@cld/component-enhancer';
import StyledTreeNode from './StyledTreeNode';
import StyledChildrenWrapper from './StyledChildrenWrapper';
import StyledToggleWrapper from './StyledToggleWrapper';
import ToggleButton from './ToggleButton';
import treeNodeDefaults from './defaults';
import treeNodePropTypes from './propTypes';

const TreeNode = memo(
  asStyledComponent(
    ({
      size,
      toggleIcon,
      nodeId,
      depth,
      visible,
      selected,
      expanded,
      noChildren,
      progress,
      onSelect,
      onExpand,
      onCollapse,
      onMore,
      setSelected,
      setExpanded,
      children,
      ...other
    }) => {
      const selectHandler = useCallback(() => {
        setSelected && setSelected(nodeId);
        onSelect && onSelect({ nodeId, selected });
      }, [setSelected, nodeId, onSelect, selected]);

      const toggleHandler = useCallback(
        (e) => {
          setExpanded && setExpanded(nodeId, !expanded);
          !expanded ? onExpand && onExpand({ nodeId }) : onCollapse && onCollapse({ nodeId });
          e.stopPropagation();
        },
        [onExpand, onCollapse, nodeId, expanded, setExpanded]
      );

      return (
        <>
          {visible ? (
            <StyledTreeNode {...{ ...other, selected, size, depth }} onClick={selectHandler}>
              <StyledToggleWrapper size={size}>
                {!noChildren && (
                  <ToggleButton
                    size={size}
                    progress={progress}
                    expanded={expanded}
                    toggleIcon={toggleIcon}
                    onClick={!progress ? toggleHandler : undefined}
                  />
                )}
              </StyledToggleWrapper>
              <StyledChildrenWrapper size={size}>
                {isFunction(children) ? children({ nodeId, depth, visible, selected, expanded, noChildren, progress, ...other }) : children}
              </StyledChildrenWrapper>
            </StyledTreeNode>
          ) : null}
        </>
      );
    }
  )
);

TreeNode.defaultProps = treeNodeDefaults;
TreeNode.propTypes = treeNodePropTypes;

export const getTreeNodeProps = ({
  nodeId,
  depth,
  setExpanded,
  setSelected,
  setProgress,
  getIsExpanded,
  getIsVisible,
  getIsSelected,
  getIsProgress,
  ...props
}) => {
  return {
    nodeId,
    depth,
    ...(getIsVisible ? { visible: getIsVisible(nodeId, depth) } : {}),
    ...(getIsExpanded ? { expanded: getIsExpanded(nodeId, depth) } : {}),
    ...(getIsSelected ? { selected: getIsSelected(nodeId) } : {}),
    ...(getIsProgress ? { progress: getIsProgress(nodeId) } : {}),
    setExpanded,
    setSelected,
    setProgress,
    ...props,
  };
};

export default memo((props) => <TreeNode {...getTreeNodeProps(props)} />);
